<!--
 * @Descripttion: 手动创建企业策划（IAS 服务商版）
 * @Date: 2026-07-28
-->
<template>
  <div class="a-plan-manual box-flex-column" v-xrk-loading="loading">
    <div class="page-header">
      <div class="page-header__left">
        <h1 class="page-title">
          {{ planId ? '查看企业策划' : '新增企业策划' }}
        </h1>
        <div class="page-header__info">
          <template v-if="planId">
            <span class="info-item">
              工业名称：
              <b>{{ industryName || '--' }}</b>
            </span>
          </template>
          <p v-else class="page-tip">
            选择工业后，点击「选择场景」从场景库挑选推荐场景，确认后进入策划列表统一管理。
          </p>
        </div>
      </div>
      <div class="page-header__actions">
        <BaseButton type="default" @click="onCancel">返回</BaseButton>
        <BaseButton type="primary" :click="onSave">保存</BaseButton>
      </div>
    </div>

    <div class="table-wrap">
      <XrkTable
        ref="xrkTableRef"
        :getListAjax="getListAjax"
        :searchConfig="searchConfig"
        :tableConfig="tableConfig"
        :paginationConfig="paginationConfig"
      >
        <template #tableBefore>
          <div class="scene-bar">
            <BaseButton type="primary" @click="onOpenPicker">
              选择场景
            </BaseButton>
            <span class="scene-count">
              已选择
              <b>{{ planData.length }}</b>
              个场景
            </span>
          </div>
        </template>
      </XrkTable>
    </div>
  </div>
</template>

<script lang="tsx" setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  BaseButton,
  useCreateModal,
  useSelectColumn,
  XrkMessage,
  XrkTable,
  XrkTableProps,
  XrkTableRef
} from 'xrk-components';
import { storeToRefs } from 'pinia';
import appStore from '@/store';
import { deptListPageCmb } from '@/views/form-manage/src/data-source/apis';
import { sceneManageDetail, sceneManageSave } from '../data-source/apis';
import { sceneManageToDisplayTranslator } from '../data-source/translators';
import {
  expandDetailCol,
  indexCol,
  linkedProductCol,
  sceneNameCol,
  medicalRepTagCol,
  terminalServiceCol,
  projectIntroCol,
  platformPriceCol,
  updateDtCol
} from '../fields/columns';
import type {
  SceneFlatItem,
  SceneManageProductItem
} from '../data-source/types';
import SceneSelectDialog from '../components/scene-select-dialog.vue';

const route = useRoute();
const router = useRouter();
const { info: userInfo } = storeToRefs(appStore.useUserStoreSetup);

const loading = ref(false);
const industryId = ref<number | undefined>();
const industryName = ref('');
const planId = ref<number | undefined>();
const planData = ref<SceneFlatItem[]>([]);
/** 原始勾选数据（SceneManageProductItem[]，保存时整包提交） */
const productsData = ref<SceneManageProductItem[]>([]);
/** 编辑模式乐观锁：detail 返回的主表 updateDt，保存时作为 lastUpdateDt 传回 */
const lastUpdateDt = ref<string>('');
const xrkTableRef = ref<XrkTableRef>(null);

const aspId = computed(() => Number((userInfo.value as any)?.finalDepId) || 0);

// ======================== XrkTable 配置 ========================

const paginationConfig: XrkTableProps['paginationConfig'] = {
  paginationPageSize: 15
};

const columns = [
  expandDetailCol,
  indexCol,
  linkedProductCol,
  sceneNameCol,
  medicalRepTagCol,
  terminalServiceCol,
  projectIntroCol,
  platformPriceCol,
  updateDtCol
];

const tableConfig: XrkTableProps['tableConfig'] = {
  baseConfig: { rowKey: '_id' },
  tableColumns: columns
};

/** 前端本地分页：全量数据在 planData 中，翻页做本地切片 */
const getListAjax = async (params: Record<string, any> = {}) => {
  const { pageNum = 1, pageSize = 15 } = params || {};
  const list = planData.value;
  const start = (pageNum - 1) * pageSize;
  return {
    data: {
      list: list.slice(start, start + pageSize),
      total: list.length
    }
  };
};

// ======================== XrkTable 配置 ========================

/** 新建模式：搜索区承载工业选择；编辑模式：隐藏搜索区（页头只读展示） */
const searchConfig = computed<XrkTableProps['searchConfig']>(() => ({
  hide: !!planId.value,
  searchBtn: { show: false },
  resetBtn: { show: false },
  searchColumns: [
    useSelectColumn({
      prop: 'industryId',
      label: '工业名称',
      config: {
        placeholder: '请选择工业名称',
        clearable: true,
        filterUseRemote: true,
        labelKey: 'entpName',
        valueKey: 'deptId',
        remoteMethod: (name: string) =>
          deptListPageCmb({
            pageNum: 1,
            pageSize: 50,
            enterpriseName: name || '',
            aspId: userInfo.value.finalDepId
          }).then((res: any) => {
            const list = res?.data?.list || [];
            // 只保留工业（functionCode=1），过滤服务商/供应商
            res.data.list = list.filter((e: any) => +e.functionCode === 1);
            return res;
          })
      },
      listener: {
        onChange: (_val: any, _searchData: any) => {
          industryName.value = _searchData?.[0]?.entpName || '';
          industryId.value = _val != null ? Number(_val) : undefined;
          // 工业变化（含首次选择/清空）→ 清场景列表（新建不调 detail，数据由弹窗打开时经 product/page + scene/page 获取）
          planData.value = [];
          productsData.value = [];
          xrkTableRef.value?.initList?.();
        }
      }
    })
  ] as any[]
}));

/** translator 输出 → 表格展示行（映射 terminalChannelText/serviceObjectText 到表格列字段） */
const toSceneFlatItems = (
  products: SceneManageProductItem[]
): SceneFlatItem[] =>
  sceneManageToDisplayTranslator(products).map(scene => ({
    ...scene,
    terminalChannel: scene.terminalChannelText || '',
    serviceObject: scene.serviceObjectText || ''
  })) as unknown as SceneFlatItem[];

// ======================== 详情加载 ========================

/** 加载详情（编辑模式：有 planId 时调用，sceneManageDetail 返回 products[].scenes[] 双层结构） */
const loadDetail = async () => {
  if (!industryId.value || !planId.value) return;
  loading.value = true;
  try {
    const res = await sceneManageDetail({
      planId: planId.value,
      industryId: industryId.value,
      aspId: aspId.value || undefined,
      detailMode: 2
    });
    const detail = res.data;
    // V2：detail 只返回已配置勾选数据（无 selected）
    productsData.value = detail?.products || [];
    // 编辑模式乐观锁：记录主表 updateDt，保存时作为 lastUpdateDt 传回
    lastUpdateDt.value = detail?.updateDt || '';
    // 展示数据：translator 按 sceneId 合并（场景挂产品子集）
    planData.value = toSceneFlatItems(productsData.value);
    // 详情模式：展示接口返回的工业名称（只读）
    if (detail?.industryName) {
      industryName.value = detail.industryName;
    }
    xrkTableRef.value?.initList?.();
  } catch (error) {
    console.error('获取详情失败:', error);
    XrkMessage.error('获取详情失败');
  } finally {
    loading.value = false;
  }
};

// ======================== 选择场景 ========================

/** 打开场景选择弹窗 */
const onOpenPicker = () => {
  if (!industryId.value) {
    XrkMessage.warning('请先选择工业名称');
    return;
  }

  useCreateModal(SceneSelectDialog, {
    products: productsData.value,
    industryName: industryName.value,
    industryId: industryId.value,
    aspId: aspId.value || undefined,
    planId: planId.value,
    onConfirm: (products: SceneManageProductItem[]) => {
      onSceneConfirm(products);
    }
  });
};

/** 场景选择弹窗确认回调：用 emit 全量数据替换本地 products + translator 重塑展示（不再调接口，保存统一走右上角按钮） */
const onSceneConfirm = (products: SceneManageProductItem[]) => {
  productsData.value = products;
  planData.value = toSceneFlatItems(products);
  xrkTableRef.value?.initList?.();
};

// ======================== 保存 ========================

/** 页面右上角保存：校验工业+场景数据后调 config/save 全量更新 */
const onSave = async () => {
  if (!industryId.value) {
    XrkMessage.warning('请先选择工业名称');
    return;
  }
  if (productsData.value.length === 0) {
    XrkMessage.warning('请先选择场景');
    return;
  }
  loading.value = true;
  try {
    // V2 整包保存：只提交勾选数据（无 selected）；编辑时传 lastUpdateDt 乐观锁；totalBudget 需求不做不传
    const res = await sceneManageSave({
      planScope: 2,
      industryId: industryId.value,
      aspId: aspId.value,
      createType: 1,
      planId: planId.value || null,
      lastUpdateDt: lastUpdateDt.value || undefined,
      products: productsData.value
    });
    if (!planId.value && res.data?.planId) {
      planId.value = res.data.planId;
    }
    XrkMessage.success('保存成功');
    // 保存后回到列表
    router.push({
      path: '/main/a/scene-mgmt/main',
      query: { tab: route.query.tab }
    });
  } catch (error) {
    console.error('保存失败:', error);
    XrkMessage.error('保存失败');
  } finally {
    loading.value = false;
  }
};

// ======================== 取消 ========================

const onCancel = () => {
  router.push({
    path: '/main/a/scene-mgmt/main',
    query: { tab: route.query.tab }
  });
};

// ======================== 初始化 ========================

onMounted(() => {
  // 编辑模式：从 query 读取 planId/industryId，加载详情
  const queryPlanId = route.query.planId;
  const queryIndustryId = route.query.industryId;
  if (queryPlanId) {
    planId.value = Number(queryPlanId);
  }
  if (queryIndustryId) {
    industryId.value = Number(queryIndustryId);
  }
  if (planId.value && industryId.value) {
    loadDetail();
  }
});
</script>

<style lang="scss" scoped>
.a-plan-manual {
  height: 100%;
  padding: 20px;
  box-sizing: border-box;
  overflow: hidden;
}
.page-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  background: #fff;
  border-radius: 4px;
  padding: 20px 0;
  flex-shrink: 0;
}
.page-title {
  margin: 0 0 10px;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}
.page-tip {
  margin: 8px 0 0;
  font-size: 12px;
  color: #9ca3af;
}
.page-header__actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}
.table-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #fff;
  border-radius: 4px;
  padding: 20px;
}
.scene-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}
.scene-count {
  font-size: 13px;
  color: #8a9099;

  b {
    color: #1677ff;
  }
}

/* ========== 页头信息（编辑模式只读展示） ========== */

.page-header__info {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-top: 4px;
}

.info-item {
  font-size: 13px;
  color: #6b7280;

  b {
    color: #1f2937;
    font-weight: 500;
  }
}

.page-tip {
  margin: 4px 0 0;
  font-size: 12px;
  color: #9ca3af;
}

/* ========== 关联产品多组展示 ========== */

.linked-products {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.linked-product {
  min-height: 28px;
  display: flex;
  align-items: center;
  font-size: 12px;

  b {
    margin-right: 6px;
  }
}

/* ========== 备案类型徽章 ========== */

.ipc-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  background: #f0f2f5;
  color: #6b7079;

  &.need {
    color: #c2410c;
    background: #fff3e8;
  }
}

/* ========== 终端渠道/服务对象合并列 ========== */

.terminal-service-cell {
  white-space: normal;
  line-height: 1.65;
}

/* ========== expand 推荐理由/输出成果 ========== */

.reason-expand {
  background: #f6f9ff;
  color: #4a5568;
  font-size: 12.5px;
  line-height: 1.7;
  padding: 10px 14px;

  &__item {
    display: block;

    & + & {
      margin-left: 0;
    }
  }

  &__icon {
    margin-right: 4px;
  }
}
</style>
